import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Icons as I } from '../pack-icons';
import { useGenerationStatus } from '../../state/GenerationStatusContext.jsx';
import OperationRow from './OperationRow.jsx';

const MAX_EXPANDED_ROWS = 3;
// New ops expand the plate for this long before auto-collapsing to a pill.
const ATTENTION_EXPAND_MS = 5000;

export default function GenerationStatusPlate() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const {
    visibleOperations,
    activeOperations,
    recentlyCompleted,
    collapsed,
    setCollapsed,
    dismissOperation,
  } = useGenerationStatus();

  // The Batch detail page already renders the same generation progress
  // inline (Live · 1 of 2 card, Ready / In Flight / Queued stats, per-tile
  // status). The floating plate on top is redundant noise here, and at
  // mobile widths it pins to the bottom and visually clashes with the
  // next batch tile. Suppress it on this route — every other screen still
  // gets the global plate so users can track work from elsewhere.
  const isBatchDetail = pathname?.startsWith('/app/batch/');

  const [autoExpand, setAutoExpand] = useState(false);
  const lastSeenIdsRef = useRef(new Set());
  // Hover is a ref, NOT state, on purpose: it must not feed the expanded/
  // collapsed render decision. It used to be OR-ed into `expanded`, which made
  // the collapse arrow feel dead — clicking it keeps the cursor over the card,
  // so `hovering` held the card open until you moved the mouse away, and the
  // click looked like it did nothing. Now hover only PAUSES the attention
  // auto-collapse timer so a quick read isn't cut off.
  const hoveringRef = useRef(false);

  // Pop the plate open briefly whenever a new op appears, then auto-collapse
  // back to the pill. Hovering pauses the auto-collapse; it re-collapses on
  // mouse-leave (see onPlateLeave).
  useEffect(() => {
    if (!visibleOperations.length) {
      lastSeenIdsRef.current = new Set();
      return undefined;
    }
    const seen = lastSeenIdsRef.current;
    const incoming = visibleOperations.filter((op) => !seen.has(op.id));
    visibleOperations.forEach((op) => seen.add(op.id));
    if (!incoming.length) return undefined;
    setAutoExpand(true);
    const t = setTimeout(() => {
      if (!hoveringRef.current) setAutoExpand(false);
    }, ATTENTION_EXPAND_MS);
    return () => clearTimeout(t);
  }, [visibleOperations]);

  // Auto-collapse to the compact strip whenever the user navigates to another
  // screen — the expanded card should never sit on top of wherever they just
  // went. They can re-open it from the strip. Skips the first render so a
  // freshly-started generation's attention pop isn't immediately squashed.
  const lastPathRef = useRef(pathname);
  useEffect(() => {
    if (lastPathRef.current === pathname) return;
    lastPathRef.current = pathname;
    setAutoExpand(false);
    setCollapsed(true);
  }, [pathname, setCollapsed]);

  if (!visibleOperations.length) return null;
  if (isBatchDetail) return null;

  // Expanded when the user hasn't collapsed it, OR a new op just popped it open
  // for attention. Hover is deliberately absent (see hoveringRef) so the
  // collapse/expand arrows take effect on the very first click.
  const expanded = !collapsed || autoExpand;
  const visible = visibleOperations.slice(0, MAX_EXPANDED_ROWS);
  const overflow = Math.max(0, visibleOperations.length - visible.length);

  // Hover only gates the attention timer + collapses the transient pop on
  // leave. An explicit open (collapsed === false) is unaffected by leave.
  const onPlateEnter = () => { hoveringRef.current = true; };
  const onPlateLeave = () => { hoveringRef.current = false; setAutoExpand(false); };

  // Collapsed pill — single line with status + chevron.
  if (!expanded) {
    const runningCount = activeOperations.length;
    const doneCount = recentlyCompleted.filter((op) => op.status === 'succeeded').length;
    const failCount = recentlyCompleted.filter((op) => op.status === 'failed').length;
    const summary = runningCount > 0
      ? (runningCount === 1
          ? activeOperations[0].label
          : t('generationStatusPlate.operationsInProgress', { count: runningCount }))
      : failCount > 0
        ? t('generationStatusPlate.countFailed', { count: failCount })
        : doneCount > 0
          ? t('generationStatusPlate.countJustCompleted', { count: doneCount })
          : t('generationStatusPlate.backgroundActivity');
    return (
      <div className="gen-plate-wrap">
        <div
          className="gen-strip"
          role="status"
          onMouseEnter={onPlateEnter}
          onMouseLeave={onPlateLeave}
        >
          <button
            type="button"
            className="gen-strip-btn"
            onClick={() => setCollapsed(false)}
            aria-label={t('generationStatusPlate.showActivity')}
          >
            <span className="gen-strip-dot pulse" aria-hidden />
            <span className="gen-strip-label">{summary}</span>
            {runningCount > 1 && <span className="gen-strip-count">{runningCount}</span>}
            <I.chevDown size={12} className="gen-strip-chev" />
          </button>
        </div>
      </div>
    );
  }

  // Expanded card.
  const headlineLabel = visibleOperations.length === 1
    ? t('generationStatusPlate.activity')
    : t('generationStatusPlate.activitiesCount', { count: visibleOperations.length });

  return (
    <div className="gen-plate-wrap">
      <div
        className="gen-plate-card"
        role="region"
        aria-label={t('generationStatusPlate.backgroundActivity')}
        onMouseEnter={onPlateEnter}
        onMouseLeave={onPlateLeave}
      >
        <div className="gen-plate-head">
          <div className="gen-plate-title">
            <span className="gen-plate-dot" aria-hidden />
            <span>{headlineLabel}</span>
            {visibleOperations.length > 1 && (
              <span className="gen-plate-count">{visibleOperations.length}</span>
            )}
          </div>
          <button
            type="button"
            className="gen-plate-collapse"
            onClick={() => { setAutoExpand(false); setCollapsed(true); }}
            aria-label={t('generationStatusPlate.collapse')}
            title={t('generationStatusPlate.collapse')}
          >
            <I.chevUp size={13} />
          </button>
        </div>
        <div className="gen-plate-rows">
          {visible.map((op) => (
            <OperationRow key={op.id} op={op} onDismiss={dismissOperation} />
          ))}
        </div>
        {overflow > 0 && (
          <div className="gen-plate-more">{t('generationStatusPlate.overflowMore', { count: overflow })}</div>
        )}
      </div>
    </div>
  );
}
